import { useEffect, useState } from 'react';
import type { BFIXRateType } from '../types/BFIXRateType';
import { getBfixRateTypes } from '../api/bfixRateTypes';

// Module-level cache. Same pattern as useIdxTypes / useAssetTypes / etc.
// Feeds the bfixRateTypeId dropdown on BFIXSpecialDaysPage.
let cache: BFIXRateType[] | null = null;
let inflight: Promise<BFIXRateType[]> | null = null;

function loadBfixRateTypes(): Promise<BFIXRateType[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getBfixRateTypes()
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function clearBfixRateTypesCache(): void {
  cache = null;
  inflight = null;
}

interface UseBfixRateTypesResult {
  data: BFIXRateType[] | null;
  loading: boolean;
  error: string | null;
}

export function useBfixRateTypes(): UseBfixRateTypesResult {
  const [state, setState] = useState<UseBfixRateTypesResult>(() =>
    cache
      ? { data: cache, loading: false, error: null }
      : { data: null, loading: true, error: null },
  );

  useEffect(() => {
    if (cache) {
      setState((prev) =>
        prev.data === cache
          ? prev
          : { data: cache, loading: false, error: null },
      );
      return;
    }

    let cancelled = false;
    loadBfixRateTypes()
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, loading: false, error: message });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
